import { maskCurrencyBRL, parseCurrencyBRL } from '../../../shared/utils/validators';

/** Formatações do módulo — em um lugar, para tabela e detalhe não divergirem. */

/**
 * Ponte entre o campo mascarado e a API.
 *
 * A máscara compartilhada (`maskCurrencyBRL`) produz o formato de leitura
 * brasileiro — `4.500,00`. A API espera decimal com ponto e duas casas
 * (`4500.00`), que é o mesmo formato do `numeric(14,2)` e da validação
 * do DTO. Sem esta conversão no envio, o servidor devolveria 400 para um
 * valor que o usuário digitou certo.
 */
export function currencyInputToApi(masked?: string | null): string | undefined {
  if (!masked?.trim()) return undefined;
  const numeric = parseCurrencyBRL(masked);
  if (Number.isNaN(numeric)) return undefined;
  return numeric.toFixed(2);
}

/** Caminho inverso, para preencher o campo ao editar um registro salvo. */
export function apiToCurrencyInput(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '';
  // `maskCurrencyBRL` consome dígitos da direita, então o valor entra em
  // centavos: 4500.00 → "450000" → "4.500,00".
  return maskCurrencyBRL(String(Math.round(numeric * 100)));
}

/**
 * Cidade e UF viajam num único campo de texto (`city` do candidato e da
 * vaga) no formato `Cidade/UF`. O parse também aceita `Cidade - UF`, que
 * aparece em cadastros antigos digitados à mão.
 */
export function parseCidadeUf(value?: string | null): { cidade: string; uf: string } | null {
  const match = (value ?? '').trim().match(/^(.+?)\s*[/-]\s*([A-Za-z]{2})$/);
  if (!match) return null;
  return { cidade: match[1].trim(), uf: match[2].toUpperCase() };
}

export function formatCidadeUf(cidade: string, uf: string): string {
  return `${cidade.trim()}/${uf.trim().toUpperCase()}`;
}

/** Identificação do cliente da vaga: código + nome fantasia (ou razão social). */
export function formatClienteLabel(
  client?: { code?: string | null; razaoSocial?: string | null; nomeFantasia?: string | null } | null,
): string {
  if (!client) return '—';
  const nome = client.nomeFantasia?.trim() || client.razaoSocial?.trim() || '';
  if (client.code && nome) return `${client.code} — ${nome}`;
  return client.code || nome || '—';
}

export function formatCurrencyBRL(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '—';
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatDateBR(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

export function formatDateTimeBR(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Horas em "3h" / "2d 4h" — tempo médio por etapa fica legível. */
export function formatHours(hours?: number | null): string {
  if (hours === null || hours === undefined) return '—';
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  return rest > 0 ? `${days}d ${rest}h` : `${days}d`;
}

export const JOB_TYPE_LABELS: Record<string, string> = {
  clt: 'CLT',
  pj: 'PJ',
  estagio: 'Estágio',
  temporario: 'Temporário',
  aprendiz: 'Aprendiz',
};

export const WORK_MODEL_LABELS: Record<string, string> = {
  presencial: 'Presencial',
  hibrido: 'Híbrido',
  remoto: 'Remoto',
};

export const APPROVAL_STEP_LABELS: Record<string, string> = {
  gestor: 'Gestor',
  rh: 'Recursos Humanos',
  financeiro: 'Financeiro',
  diretoria: 'Diretoria',
  recrutamento: 'Recrutamento',
};

export const REQUISITION_EVENT_LABELS: Record<string, string> = {
  criada: 'Requisição criada',
  enviada: 'Enviada para aprovação',
  passo_aprovado: 'Passo aprovado',
  reprovada: 'Reprovada',
  devolvida: 'Devolvida ao solicitante',
  reenviada: 'Reenviada para aprovação',
  aprovada: 'Aprovada — vaga criada',
  cancelada: 'Cancelada',
};

export const APPLICATION_EVENT_LABELS: Record<string, string> = {
  inscrita: 'Inscrição recebida',
  avanco: 'Avançou de etapa',
  retorno: 'Retornou à etapa anterior',
  agendamento: 'Etapa agendada',
  reagendamento: 'Etapa reagendada',
  aprovada: 'Aprovado',
  reprovada: 'Reprovado',
  banco_talentos: 'Enviado ao banco de talentos',
};
